import {
    AirspaceFeatureType,
    AIRCRAFT_TYPES,
    AircraftType,
    DistanceUnit,
    SimpleBoundingBox,
} from "@nova/constants"
import { ProjectFilterOptionId } from "./filters/projectFilterOptions"
import { DiscriminatedUnion } from "@nova/util-ts"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux"
import { memoize } from "nodash"
import { ActivatablePluginName } from "./search/registry"
import { AirspaceFeatureRef } from "features/map/layers/airspace/types"

type BboxPopup = {
    placementType: "bbox"
    bbox: SimpleBoundingBox
}
type PointPopup = {
    placementType: "point"
    point: { lat: number; lng: number }
    renderedDimensions: { width: number; height: number }
}
type LivePointPopup = {
    placementType: "live-point"
    getPoint: () => { lat: number; lng: number }
    pointRefreshRateMs: number
    renderedDimensions: { width: number; height: number }
}

export type ActivePopupState =
    | ({ margin?: { x: number; y: number } | number } & (
          | {
                type: "aircraft" | "project" | "projectCluster"
                id: string
            }
          | {
                type: "airspaceFeatureList"
                id: string
                features: AirspaceFeatureRef[]
            }
          | {
                type: "airspace"
                id: string
                source: AirspaceFeatureType
            }
      ) &
          DiscriminatedUnion<BboxPopup | PointPopup | LivePointPopup>)
    | null

export type AirspaceFeaturePanelRawTextTab = "text" | "icao" | "domestic"

export type MembersSortBy = "alphabetical" | "timeRemaining"
export type MembersSortDirection = "ascending" | "descending"

const initialState = {
    panels: {
        rightPanelOpen: true,
    },
    popups: {
        activePopup: null as ActivePopupState,
        rawTextExpanded: false,
        rawTextTab: null as AirspaceFeaturePanelRawTextTab | null,
    },
    hoveredAirspaces: [] as AirspaceFeatureRef[],
    aircraft: {
        spidertracksEnabled: false,
        types: Object.fromEntries(AIRCRAFT_TYPES.map((type) => [type, true])) as Record<
            AircraftType,
            boolean
        >,
        activeAircraftId: null as string | null,
        hoveredAircraftId: null as string | null,
        adsb: {
            enabled: false,
            isPlacingCircle: false,
            circleCenter: null as { lat: number; lng: number } | null,
            trafficUpdates: {
                enabled: true,
                altitudeMinFeet: 0,
                altitudeMaxFeet: 10_000,
                altitudeUnit: DistanceUnit.Feet,
            },
        },
    },
    projectFilter: "30days" as ProjectFilterOptionId,
    membersSort: {
        by: "alphabetical" as MembersSortBy,
        direction: "ascending" as MembersSortDirection,
    },
    search: {
        query: "",
        // Kept as a plain string here (not ActivatablePluginName) so this slice's state type never
        // structurally depends on the search plugin registry — a plugin's useSearch hook may read
        // this app's redux state via useAppSelector, which would otherwise create a type cycle back
        // through RootState -> OverviewState -> ActivatablePluginName -> the registry -> the plugin
        // itself. The precise type is restored at the selector/action boundary below.
        activePlugin: undefined as string | undefined,
        recentSearchesPanelOpen: false,
        inputFocused: false,
    },
    locationOverride: null as {
        projectId: string
        isSubmitting: boolean
    } | null,
}

export const PROJECT_POPUP_DIMENSIONS = { width: 158, height: 24 }
export type OverviewState = typeof initialState
export type AircraftTypeKey = keyof OverviewState["aircraft"]["types"]
export type MembersSort = OverviewState["membersSort"]

export const overviewSliceName = "overview"
export const {
    reducer: OverviewReducer,
    actions: {
        openRightPanel,
        closeRightPanel,
        enableAdsb,
        disableAdsb,
        setAdsbCircleCenter,
        confirmAdsbCircle,
        cancelAdsbCirclePlacement,
        beginAdsbCirclePlacement,
        setSpidertracksEnabled,
        setAircraftTypeVisible,
        setActiveAircraftId,
        setHoveredAircraftId,
        setAdsbTrafficUpdatesEnabled,
        setAdsbTrafficUpdatesAltitudeMinFeet,
        setAdsbTrafficUpdatesAltitudeMaxFeet,
        setAdsbTrafficUpdatesAltitudeUnit,
        resetAircraftFilters,
        setActivePopup,
        clearActivePopup,
        setRawTextExpanded,
        setRawTextTab,
        setHoveredAirspaces,
        setProjectFilter,
        setMembersSortBy,
        setMembersSortDirection,
        setSearchQuery,
        setActiveSearchPanelPlugin: setActiveSearchPanelPluginInternal,
        openRecentSearchesPanel,
        closeFullPanel,
        setSearchInputFocused,
        startLocationOverride,
        setLocationOverrideSubmitting,
        endLocationOverride,
    },
} = createSlice({
    name: overviewSliceName,
    initialState,
    reducers: {
        openRightPanel: (state) => {
            state.panels.rightPanelOpen = true
        },
        closeRightPanel: (state) => {
            state.panels.rightPanelOpen = false
        },
        enableAdsb: (
            state,
            action: { payload: { fallbackCenter: { lat: number; lng: number } } }
        ) => {
            state.aircraft.adsb.enabled = true
            if (!state.aircraft.adsb.circleCenter) {
                state.aircraft.adsb.isPlacingCircle = true
                state.aircraft.adsb.circleCenter = action.payload.fallbackCenter
            }
        },
        disableAdsb: (state) => {
            state.aircraft.adsb.enabled = false
            state.aircraft.adsb.isPlacingCircle = false
        },
        setAdsbCircleCenter: (state, action: { payload: { lat: number; lng: number } }) => {
            state.aircraft.adsb.circleCenter = action.payload
        },
        confirmAdsbCircle: (state) => {
            state.aircraft.adsb.isPlacingCircle = false
        },
        cancelAdsbCirclePlacement: (state) => {
            state.aircraft.adsb.enabled = false
            state.aircraft.adsb.isPlacingCircle = false
            state.aircraft.adsb.circleCenter = null
        },
        beginAdsbCirclePlacement: (state) => {
            state.aircraft.adsb.isPlacingCircle = true
        },
        setSpidertracksEnabled: (state, action: { payload: boolean }) => {
            state.aircraft.spidertracksEnabled = action.payload
        },
        setAircraftTypeVisible: (
            state,
            action: { payload: { key: AircraftTypeKey; value: boolean } }
        ) => {
            state.aircraft.types[action.payload.key] = action.payload.value
        },
        setActiveAircraftId: (
            state,
            action: { payload: OverviewState["aircraft"]["activeAircraftId"] }
        ) => {
            state.aircraft.activeAircraftId = action.payload
            if (action.payload && state.search.activePlugin) {
                state.search.activePlugin = undefined
            }
        },
        setHoveredAircraftId: (
            state,
            action: { payload: OverviewState["aircraft"]["hoveredAircraftId"] }
        ) => {
            state.aircraft.hoveredAircraftId = action.payload
        },
        setAdsbTrafficUpdatesEnabled: (state, action: { payload: boolean }) => {
            state.aircraft.adsb.trafficUpdates.enabled = action.payload
        },
        setAdsbTrafficUpdatesAltitudeMinFeet: (state, action: { payload: number }) => {
            state.aircraft.adsb.trafficUpdates.altitudeMinFeet = action.payload
        },
        setAdsbTrafficUpdatesAltitudeMaxFeet: (state, action: { payload: number }) => {
            state.aircraft.adsb.trafficUpdates.altitudeMaxFeet = action.payload
        },
        setAdsbTrafficUpdatesAltitudeUnit: (state, action: { payload: DistanceUnit }) => {
            state.aircraft.adsb.trafficUpdates.altitudeUnit = action.payload
        },
        resetAircraftFilters: (state) => {
            state.aircraft.adsb = { ...initialState.aircraft.adsb }
            state.aircraft.spidertracksEnabled = initialState.aircraft.spidertracksEnabled
            state.aircraft.types = { ...initialState.aircraft.types }
        },
        setActivePopup: (state, action: { payload: NonNullable<ActivePopupState> }) => {
            state.popups.activePopup = action.payload
            state.popups.rawTextExpanded = false
            state.popups.rawTextTab = null
        },
        clearActivePopup: (state) => {
            state.popups.activePopup = null
            state.hoveredAirspaces = []
        },
        setRawTextExpanded: (state, action: { payload: boolean }) => {
            state.popups.rawTextExpanded = action.payload
        },
        setRawTextTab: (state, action: { payload: AirspaceFeaturePanelRawTextTab }) => {
            state.popups.rawTextTab = action.payload
        },
        setHoveredAirspaces: (state, action: { payload: AirspaceFeatureRef[] }) => {
            state.hoveredAirspaces = action.payload
        },
        setProjectFilter: (state, action: { payload: ProjectFilterOptionId }) => {
            state.projectFilter = action.payload
        },
        setMembersSortBy: (state, action: { payload: MembersSortBy }) => {
            state.membersSort.by = action.payload
        },
        setMembersSortDirection: (state, action: { payload: MembersSortDirection }) => {
            state.membersSort.direction = action.payload
        },
        setSearchQuery: (state, action: { payload: string }) => {
            state.search.query = action.payload
        },
        setActiveSearchPanelPlugin: (state, action: { payload: string | undefined }) => {
            state.search.activePlugin = action.payload
            state.search.recentSearchesPanelOpen = false
            if (action.payload && state.aircraft.activeAircraftId) {
                state.aircraft.activeAircraftId = null
            }
        },
        openRecentSearchesPanel: (state) => {
            state.search.recentSearchesPanelOpen = true
            state.search.activePlugin = undefined
        },
        closeFullPanel: (state) => {
            state.search.activePlugin = undefined
            state.search.recentSearchesPanelOpen = false
        },
        setSearchInputFocused: (state, action: { payload: boolean }) => {
            state.search.inputFocused = action.payload
        },
        startLocationOverride: (state, action: { payload: { projectId: string } }) => {
            state.locationOverride = {
                projectId: action.payload.projectId,
                isSubmitting: false,
            }
            state.popups.activePopup = null
            state.hoveredAirspaces = []
        },
        setLocationOverrideSubmitting: (state, action: { payload: boolean }) => {
            if (state.locationOverride) state.locationOverride.isSubmitting = action.payload
        },
        endLocationOverride: (state) => {
            state.locationOverride = null
        },
    },
})

// Re-exposes setActiveSearchPanelPluginInternal with the precise, registry-derived payload type for
// callers. This has to live outside the createSlice(...) call to avoid a circular dependency on ActivatablePluginName
export const setActiveSearchPanelPlugin = (pluginName: ActivatablePluginName | undefined) =>
    setActiveSearchPanelPluginInternal(pluginName)

const root = (s: RootState) => s[overviewSliceName]

export const selectRightPanelOpen = createSelector([root], (s) => s.panels.rightPanelOpen)
export const selectActivePopup = (s: RootState) => s[overviewSliceName].popups.activePopup
export const selectRawTextExpanded = createSelector([root], (s) => s.popups.rawTextExpanded)
export const selectRawTextTab = createSelector([root], (s) => s.popups.rawTextTab)
export const selectHoveredAirspaces = createSelector([root], (s) => s.hoveredAirspaces)

export const selectAdsbEnabled = (s: RootState) => s[overviewSliceName].aircraft.adsb.enabled
export const selectAdsbIsPlacingCircle = (s: RootState) =>
    s[overviewSliceName].aircraft.adsb.isPlacingCircle
export const selectAdsbCircleCenter = (s: RootState) =>
    s[overviewSliceName].aircraft.adsb.circleCenter
export const selectAdsbTrafficUpdatesEnabled = (s: RootState) =>
    s[overviewSliceName].aircraft.adsb.trafficUpdates.enabled
export const selectAdsbTrafficUpdatesAltitudeMinFeet = (s: RootState) =>
    s[overviewSliceName].aircraft.adsb.trafficUpdates.altitudeMinFeet
export const selectAdsbTrafficUpdatesAltitudeMaxFeet = (s: RootState) =>
    s[overviewSliceName].aircraft.adsb.trafficUpdates.altitudeMaxFeet
export const selectAdsbTrafficUpdatesAltitudeUnit = (s: RootState) =>
    s[overviewSliceName].aircraft.adsb.trafficUpdates.altitudeUnit
export const selectSpidertracksEnabled = (s: RootState) =>
    s[overviewSliceName].aircraft.spidertracksEnabled
export const selectActiveAircraftId = (s: RootState) =>
    s[overviewSliceName].aircraft.activeAircraftId
export const selectHoveredAircraftId = (s: RootState) =>
    s[overviewSliceName].aircraft.hoveredAircraftId
export const selectAircraftTypeVisible = memoize((key: AircraftTypeKey) =>
    createSelector([root], (s) => s.aircraft.types[key])
)
export const selectAircraftTypes = createSelector([root], (s) => s.aircraft.types)

export const selectProjectFilter = createSelector([root], (s) => s.projectFilter)
export const selectMembersSort = createSelector([root], (s) => s.membersSort)

export const selectSearchQuery = createSelector([root], (s) => s.search.query)
export const selectActiveSearchPanelPlugin = createSelector(
    [root],
    (s) => s.search.activePlugin as ActivatablePluginName | undefined
)
export const selectRecentSearchesPanelOpen = createSelector(
    [root],
    (s) => s.search.recentSearchesPanelOpen
)
export const selectSearchInputFocused = createSelector([root], (s) => s.search.inputFocused)
export const selectIsFullPanelOpen = createSelector(
    [root],
    (s) => s.search.activePlugin !== undefined || s.search.recentSearchesPanelOpen
)

export const selectLocationOverride = (s: RootState) => s[overviewSliceName].locationOverride
export const selectIsUpdatingProjectLocation = (s: RootState) =>
    !!s[overviewSliceName].locationOverride
export const selectLocationOverrideProjectId = (s: RootState) =>
    s[overviewSliceName].locationOverride?.projectId
export const selectIsLocationOverrideSubmitting = (s: RootState) =>
    !!s[overviewSliceName].locationOverride?.isSubmitting
